import { Box, Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router";
import { Link } from "react-router-dom";
import { useCookies } from "react-cookie";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormTextField, PrimaryButton } from "../../../components/form";
import { APP_COLORS } from "../../../enums";
import { userLogin } from "../../../api/auth";
import { useMutationWithDispatch } from "../../../hooks/useMutationWithDispatch";
import { ROUTE_PATHS } from "../../../routes/paths";
import { loginSchema, LoginFormData, PAGE_CONFIG } from "./config";
import {
  handlePostLoginNavigation,
  handleRememberMeCookies,
} from "./utils";
import { LoginResponse, OutletContext } from "./types";

function Login() {
  const { setPageConfig } = useOutletContext<OutletContext>();
  const [cookies, setCookie, removeCookie] = useCookies(["user"]);
  const navigate = useNavigate();

  const cookieEmail = (cookies as Record<string, string>)?.email || "";
  const cookiePassword = (cookies as Record<string, string>)?.password || "";

  const initialFormValues = useMemo(
    () => ({
      email: cookieEmail,
      password: cookiePassword,
    }),
    [cookieEmail, cookiePassword]
  );

  const [rememberMe, setRememberMe] = useState(() =>
    Boolean(cookieEmail)
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: initialFormValues,
  });

  const { mutateAsync: submitLogin, isPending: submitting } =
    useMutationWithDispatch<LoginResponse, LoginFormData>((payload) =>
      userLogin(payload)
    );

  useEffect(() => {
    setPageConfig({ title: PAGE_CONFIG.TITLE });
  }, [setPageConfig]);

  useEffect(() => {
    if (rememberMe && cookieEmail) {
      setValue("email", cookieEmail);
      setValue("password", cookiePassword);
    }
  }, [rememberMe, cookieEmail, cookiePassword, setValue]);

  const onSubmit = useCallback(
    async (data: LoginFormData) => {
      try {
        const payload = {
          email: data.email.toLowerCase(),
          password: data.password,
        };

        const res = await submitLogin(payload);

        if (!res?.success || !res?.data) {
          return;
        }

        handleRememberMeCookies(rememberMe, data, { setCookie, removeCookie });
        handlePostLoginNavigation(res.data, navigate);
      } catch (error) {
      }
    },
    [rememberMe, setCookie, removeCookie, submitLogin, navigate]
  );

  const onRememberMeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setRememberMe(e.target.checked);
    },
    []
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack>
        <FormTextField
          name="email"
          control={control}
          label="Email"
          required
          error={errors.email?.message}
          textFieldProps={{ type: "email" }}
        />

        <FormTextField
          name="password"
          control={control}
          label="Password"
          required
          error={errors.password?.message}
          textFieldProps={{ type: "password" }}
        />

        <Stack gap={2}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <FormControlLabel
              control={
                <Checkbox checked={rememberMe} onChange={onRememberMeChange} />
              }
              label={
                <Typography variant="medium" color="custom.lightBlack">
                  Remember me
                </Typography>
              }
            />
            <Link
              to={ROUTE_PATHS.AUTH.FORGOT_PASSWORD}
              style={{ textDecoration: "none" }}
            >
              <Typography
                variant="body-ibmPlexMono"
                color={APP_COLORS.secondary}
              >
                Forgot Password
              </Typography>
            </Link>
          </Box>

          <PrimaryButton loading={submitting}>Log In</PrimaryButton>

          <Typography variant="medium" sx={{ textAlign: "center" }}>
            Don't Have an account,
            <Link
              to={ROUTE_PATHS.AUTH.SIGNUP}
              style={{ textDecoration: "none" }}
            >
              <Typography
                component="span"
                sx={{ ml: 1, cursor: "pointer", display: "inline" }}
                variant="body-ibmPlexMono-medium"
                color={APP_COLORS.secondaryDark}
              >
                Sign Up
              </Typography>
            </Link>
          </Typography>
        </Stack>
      </Stack>
    </form>
  );
}

export default Login;
